/**
 * Stripped down version of front-end/dashboard/redux/thunk-actions/workflow-v2.ts
 * used in InquiryTemplateEditor to update, publish, and rollout
 * inquiry template-related workflow versions
 */
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { TriggerDirect } from 'dashboard/models/trigger';
import { Workflow, WorkflowAttributes } from 'dashboard/models/workflow';
import {
  WorkflowVersion,
  WorkflowVersionAttributes,
  WorkflowVersionStatus,
} from 'dashboard/models/workflow-version';
import { ApiModel, ApiPayload, ApiResource, ApiSingletonPayload } from 'lib/interfaces/api';

/**
 * @name saveWorkflowVersion
 * @param workflowId ID of Workflow
 * @param workflowVersion Workflow Version to save
 *
 * If editing a published version:
 * 1. Create a new workflow version (will inherit from published version)
 * 2. Update the newly created version with updated values
 *
 * If editing a newly created version (draft):
 * 1. Update with form values
 */
export function saveWorkflowVersion({
  workflowVersion,
  withWorkflowNameUpdate,
  environmentId,
  inquiryTemplateId,
}: {
  workflowVersion: WorkflowVersion;
  withWorkflowNameUpdate: boolean;
  environmentId: string;
  inquiryTemplateId: string;
}) {
  return async () => {
    const workflowId = workflowVersion.relationships.workflow?.data?.id;
    if (withWorkflowNameUpdate) {
      SessionClientDashboard.updateResource<Workflow>(
        {
          type: ApiModel.Workflow,
          id: workflowId,
          attributes: { name: workflowVersion.attributes['workflow-name'] },
        },
        { meta: { 'source-id': inquiryTemplateId } },
      );
    }
    const workflowVersionUpdated = workflowId
      ? await saveVersion(workflowId, workflowVersion, inquiryTemplateId)
      : await createWorkflow(workflowVersion, environmentId, inquiryTemplateId);
    return { originalId: workflowVersion.id, updatedId: workflowVersionUpdated.id };
  };
}

async function createWorkflow(
  workflowVersion: WorkflowVersion,
  environmentId: string,
  inquiryTemplateId: string,
): Promise<WorkflowVersion> {
  const { included } = await SessionClientDashboard.postResourceCreate<
    Workflow,
    ApiSingletonPayload<Partial<WorkflowAttributes>>
  >(
    {
      type: ApiModel.Workflow,
      attributes: {
        version: 2,
        'environment-id': environmentId,
      },
    },
    { meta: { 'trigger-type': ApiModel.TriggerDirect, 'source-id': inquiryTemplateId } },
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const createdWorkflowVersion = included.find(
    (include: ApiResource) => include.type === ApiModel.WorkflowVersion,
  ) as WorkflowVersion;
  const workflowVersionId = createdWorkflowVersion.id;

  const { data: trigger } = await SessionClientDashboard.postResourceCreate<
    TriggerDirect,
    ApiSingletonPayload<{ 'environment-id': string; 'source-id': string }>
  >({
    type: ApiModel.TriggerDirect,
    attributes: {
      'environment-id': environmentId,
      'source-id': inquiryTemplateId,
    },
  });

  const { data: workflowVersionUpdated } = await SessionClientDashboard.updateResource<
    WorkflowVersion,
    ApiPayload<
      Partial<WorkflowVersionAttributes> & {
        'trigger-id': string;
        'trigger-type': ApiModel;
      }
    >
  >(
    {
      type: ApiModel.WorkflowVersion,
      id: workflowVersionId,
      attributes: {
        // these are the only three attributes that are updatable
        // FIXME: Deep underscore nested KVP
        'steps-config': workflowVersion.attributes['steps-config'],
        outputs: workflowVersion.attributes.outputs,
        'trigger-id': trigger.id,
        'trigger-type': ApiModel.TriggerDirect,
      },
    },
    { meta: { 'source-id': inquiryTemplateId } },
  );

  return workflowVersionUpdated;
}

// If workflow not created (workflowId = null), created workflow, and update version
// If published, dup then save. Else just save.
async function saveVersion(
  workflowId: string,
  workflowVersion: WorkflowVersion,
  inquiryTemplateId: string,
): Promise<WorkflowVersion> {
  const workflowVersionId =
    workflowVersion.attributes.status === WorkflowVersionStatus.Published
      ? (
          await SessionClientDashboard.postResourceCreate<
            WorkflowVersion,
            {
              type: ApiModel.WorkflowVersion;
              attributes: { 'workflow-id': string; 'src-workflow-version-id': string };
            }
          >(
            {
              type: ApiModel.WorkflowVersion,
              attributes: {
                'workflow-id': workflowId,
                'src-workflow-version-id': workflowVersion.id,
              },
            },
            { meta: { 'trigger-type': ApiModel.TriggerDirect, 'source-id': inquiryTemplateId } },
          )
        ).data.id
      : workflowVersion.id;

  const { data: workflowVersionUpdated } =
    await SessionClientDashboard.updateResource<WorkflowVersion>(
      {
        type: ApiModel.WorkflowVersion,
        id: workflowVersionId,
        attributes: {
          // these are the only three attributes that are updatable
          // FIXME: Deep underscore nested KVP
          'steps-config': workflowVersion.attributes['steps-config'],
          outputs: workflowVersion.attributes.outputs,
        },
      },
      { meta: { 'source-id': inquiryTemplateId } },
    );

  return workflowVersionUpdated;
}
